import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { VisitAccessPoint } from 'services/access-points/access-points.service';
import { useAPIAccessPoints } from 'services/access-points/api-access-points.service';
import { useAPISingleUsePasses } from 'services/single-use-passes/api-single-use-passes.service';
import { useAPIVisitorPasses } from 'services/visitor-passes/api-visitor-passes.service';
import { onSharePasscode } from 'mobile/passcodes/passcode-functions';
import { useTranslator } from 'tools/view-hooks/translator-hook';
import { deviceOffset } from 'tools/date/site-time';
import { EndPreset, StartMode } from 'fragments/passcodes/fragments/visitor-pass-creation/pass-window';
import { capabilitiesFor } from './point-capabilities';
import { buildVisitPayload, buildVisitSummary, isDraftInFuture, VisitDraft } from './visit-summary';
import { VisitAccessController, VisitCreated, VisitValidationError } from './interfaces';

export const useVisitAccessController = (
    pointsService = useAPIAccessPoints(),
    singleUseService = useAPISingleUsePasses(),
    visitorPassService = useAPIVisitorPasses(),
): VisitAccessController => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { translate } = useTranslator();
    const accessToken = searchParams.get('access_token') || '';

    const [step, setStep] = useState<0 | 1>(0);
    const [points, setPoints] = useState<VisitAccessPoint[]>([]);
    const [pointsLoaded, setPointsLoaded] = useState(false);
    const [pointsLoadError, setPointsLoadError] = useState(false);
    const [selectedPoint, setSelectedPoint] = useState<VisitAccessPoint | undefined>();
    const [pickerOpen, setPickerOpen] = useState(false);
    const [visitorName, setVisitorName] = useState('');
    const [panelOpen, setPanelOpen] = useState(false);
    const [startMode, setStartMode] = useState<StartMode>('now');
    const [startDate, setStartDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endPreset, setEndPreset] = useState<EndPreset>('p1');
    const [endDate, setEndDate] = useState('');
    const [endTime, setEndTime] = useState('');
    const [maxUses, setMaxUses] = useState<number | undefined>(undefined);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasNetworkError, setHasNetworkError] = useState(false);
    const [created, setCreated] = useState<VisitCreated | undefined>();
    // Nada acá dispara un re-render sólo porque pasó el tiempo. submit() lo usa
    // para forzar uno cuando encuentra la ventana vencida, así el usuario ve el
    // error en vez de un fallo mudo.
    const [, forceRerender] = useState(0);

    // Reinvocable: es el punto de reintento cuando la carga falla por red. Sin
    // esto un hipo de conexión deja la pantalla inservible para siempre.
    const loadPoints = () => {
        setPointsLoadError(false);
        pointsService
            .listVisitAccessPoints(accessToken)
            .then((list) => {
                setPoints(list);
                setPointsLoaded(true);
                if (list.length === 1) {
                    setSelectedPoint(list[0]);
                }
            })
            .catch(() => {
                setPointsLoadError(true);
                setPointsLoaded(true);
            });
    };

    useEffect(() => {
        if (!accessToken) {
            navigate('/mobile/404');
            return;
        }
        loadPoints();
    }, []);

    const capabilities = selectedPoint ? capabilitiesFor(selectedPoint.kind) : undefined;

    const draft: VisitDraft | undefined = selectedPoint
        ? {
              point: selectedPoint,
              visitorName,
              startMode,
              startDate,
              startTime,
              endPreset,
              endDate,
              endTime,
              maxUses,
          }
        : undefined;

    const isExactComplete =
        (startMode !== 'exact' || (!!startDate && !!startTime)) &&
        // El vencimiento exacto sólo cuenta donde se puede elegir: en un teclado
        // ese valor puede estar cargado de un punto anterior y no se usa.
        (!capabilities?.canChooseEnd || endPreset !== 'exact' || (!!endDate && !!endTime));

    // Sin useMemo a propósito: "ahora" cambia sin que ninguna dependencia cambie,
    // y memoizarlo congelaba el chequeo en el resultado del primer render.
    const validationError: VisitValidationError | undefined = (() => {
        if (!selectedPoint || !draft) {
            return 'point';
        }
        if (!visitorName.trim()) {
            return 'name';
        }
        if (!isExactComplete) {
            return 'incomplete';
        }
        if (!isDraftInFuture(draft)) {
            return 'range';
        }
        return undefined;
    })();

    const canSubmit = !validationError && !isSubmitting;

    // Sin useMemo a propósito, igual que validationError: buildVisitSummary
    // consulta el reloj real para el punto con ventana fija e inicio "ahora", y
    // memoizarlo congelaba la hora de inicio del resumen en el primer render.
    const validityLabel = (() => {
        if (!draft) {
            return '';
        }
        if (!isExactComplete) {
            return translate({ key: 'visit-access.summary-incomplete' });
        }
        return buildVisitSummary(draft, translate);
    })();

    const showTimezoneNote = !!selectedPoint?.timezone && selectedPoint.timezone !== deviceOffset();

    const submit = () => {
        if (!canSubmit || !draft) {
            return;
        }
        // Revalidar acá y no confiar en el último render: entre que se abrió la
        // pantalla y se tocó el botón puede haber pasado una hora.
        if (!isDraftInFuture(draft)) {
            forceRerender((n) => n + 1);
            return;
        }
        const payload = buildVisitPayload(draft);
        setIsSubmitting(true);
        setHasNetworkError(false);

        const request =
            payload.kind === 'keypad'
                ? singleUseService
                      .createSingleUsePass(accessToken, payload.input)
                      .then((res): VisitCreated => ({ kind: 'keypad', res }))
                : visitorPassService
                      .createVisitorPass(accessToken, payload.input)
                      .then((res): VisitCreated => ({ kind: 'reception', res }));

        return request
            .then((c) => {
                setCreated(c);
                setStep(1);
            })
            .catch(() => setHasNetworkError(true))
            .finally(() => setIsSubmitting(false));
    };

    const onShare = () => {
        if (!created || !selectedPoint) {
            return;
        }
        const name = visitorName.trim();
        const message =
            created.kind === 'keypad'
                ? translate({
                      key: 'visit-access.share-message-code',
                      extra: { name, code: created.res.code, url: created.res.share_url },
                  })
                : translate({
                      key: 'visit-access.share-message-qr',
                      extra: { name, url: created.res.share_url },
                  });
        onSharePasscode({ v: 1, data: { message } });
    };

    // Sólo tiene sentido en el camino del código: un QR no se dicta por teléfono.
    const onShareCodeOnly = () => {
        if (created?.kind !== 'keypad') {
            return;
        }
        onSharePasscode({ v: 1, data: { message: created.res.code } });
    };

    return {
        step,
        points,
        pointsLoaded,
        pointsLoadError,
        selectedPoint,
        capabilities,
        pickerOpen,
        visitorName,
        panelOpen,
        startMode,
        startDate,
        startTime,
        endPreset,
        endDate,
        endTime,
        maxUses,
        isSubmitting,
        hasNetworkError,
        created,
        canSubmit,
        validationError,
        validityLabel,
        showTimezoneNote,
        setVisitorName,
        openPicker: () => setPickerOpen(true),
        closePicker: () => setPickerOpen(false),
        // No resetea nada más: la regla 2 pide conservar todo lo que sirva, y lo
        // que la puerta nueva no usa se ignora al armar el payload, no se borra.
        onPointPicked: (p: VisitAccessPoint) => {
            setSelectedPoint(p);
            setPickerOpen(false);
        },
        togglePanel: () => setPanelOpen((v) => !v),
        setStartMode,
        setStartDate,
        setStartTime,
        setEndPreset,
        setEndDate,
        setEndTime,
        setMaxUses,
        submit,
        onShare,
        onShareCodeOnly,
        retryLoadPoints: loadPoints,
        onClose: () => navigate(-1),
    };
};
